<template>
    <transition :name="`tooltip--transition--${animation || direction}`" mode="out-in">
        <span
            class="tooltip position--absolute background--green color--white"
            :class="[
                `tooltip--${direction}`,
                { [`tooltip--${direction}--${arrow}`]: arrow },
                { 'tooltip--hover': hover },
            ]"
        >
        </span>
    </transition>
</template>

<script>
export default {
    props: {
        direction: String,
        animation: String,
        arrow: String,
        hover: Boolean
    }
};
</script>

<style lang="scss">
    // =============================================================================
    // Animations
    // =============================================================================
    @include keyframes ( slide-in-right ) {
        0% {
            @include transform( translateX( -20px ) );
            opacity: 0;
        }
        50% {
            opacity: .8;
        }
        100% {
            @include transform( translateX( 0 ) );
            opacity: 1;
        }
    }

    @include keyframes ( slide-out-right ) {
        0% {
            @include transform( translateX( 0 ) );
            opacity: 1;
        }
        40% {
            opacity: .2;
        }
        100% {
            @include transform( translateX( 20px ) );
            opacity: 0;
        }
    }

    @include keyframes ( slide-in-left ) {
        0% {
            @include transform( translateX( -120px ) );
            opacity: 0;
        }
        50% {
            opacity: .8;
        }
        100% {
            @include transform( translateX( -50% ) );
            opacity: 1;
        }
    }

    @include keyframes ( slide-out-left ) {
        0% {
            @include transform( translateX( -50% ) );
            opacity: 1;
        }
        40% {
            opacity: .2;
        }
        100% {
            @include transform( translateX( -120px ) );
            opacity: 0;
        }
    }

    @include keyframes ( slide-in-top ) {
        0% {
            @include transform( translateY( 20px ) );
            opacity: 0;
        }
        50% {
            opacity: .8;
        }
        100% {
            @include transform( translateY( 0 ) );
            opacity: 1;
        }
    }

    @include keyframes ( slide-out-top ) {
        0% {
            @include transform( translateY( 0 ) );
            opacity: 1;
        }
        40% {
            opacity: .2;
        }
        100% {
            @include transform( translateY( -20px ) );
            opacity: 0;
        }
    }

    @include keyframes ( slide-in-top-center ) {
        0% {
            @include transform( translate( -50%, 20px ) );
            opacity: 0;
        }
        50% {
            opacity: .8;
        }
        100% {
            @include transform( translate( -50%, 0 ) );
            opacity: 1;
        }
    }

    @include keyframes ( slide-out-top-center ) {
        0% {
            @include transform( translate( -50%, 0 ) );
            opacity: 1;
        }
        40% {
            opacity: .2;
        }
        100% {
            @include transform( translate( -50%, -20px ) );
            opacity: 0;
        }
    }

    @include keyframes ( slide-in-bottom ) {
        0% {
            @include transform( translateY( -20px ) );
            opacity: 0;
        }
        50% {
            opacity: .8;
        }
        100% {
            @include transform( translateY( 0px ) );
            opacity: 1;
        }
    }
    @include keyframes ( slide-out-bottom ) {
        0% {
            @include transform( translateY( 0 ) );
            opacity: 1;
        }
        40% {
            opacity: .2;
        }
        100% {
            @include transform( translateY( 20px ) );
            opacity: 0;
        }
    }

    // =============================================================================
    // Style
    // =============================================================================
    .tooltip {
        &--transition {
            &--right {
                &-leave-active {
                    @include animation( slide-out-right .26s cubic-bezier( .4, .0, 1, 1 ) );
                }
                &-enter-active  {
                    @include animation( slide-in-right .26s cubic-bezier( .0, .0, .2, 1 ) );
                }
            }
            &--left {
                &-leave-active {
                    @include animation( slide-out-left .26s cubic-bezier( .4, .0, 1, 1 ) );
                }
                &-enter-active  {
                    @include animation( slide-in-left .26s cubic-bezier( .0, .0, .2, 1 ) );
                }
            }
            &--top {
                &-leave-active {
                    @include animation( slide-out-top-center .26s cubic-bezier( .4, .0, 1, 1 ) );
                }
                &-enter-active  {
                    @include animation( slide-in-top-center .26s cubic-bezier( .0, .0, .2, 1 ) );
                }
                &--base {
                    &-leave-active {
                        @include animation( slide-out-top .26s cubic-bezier( .4, .0, 1, 1 ) );
                    }
                    &-enter-active  {
                        @include animation( slide-in-top .26s cubic-bezier( .0, .0, .2, 1 ) );
                    }
                }
            }
            &--bottom {
                &-leave-active {
                    @include animation( slide-out-bottom-center .26s cubic-bezier( .4, .0, 1, 1 ) );
                }
                &-enter-active  {
                    @include animation( slide-in-bottom-center .26s cubic-bezier( .0, .0, .2, 1 ) );
                }
                &--base {
                    &-leave-active {
                        @include animation( slide-out-bottom .26s cubic-bezier( .4, .0, 1, 1 ) );
                    }
                    &-enter-active  {
                        @include animation( slide-in-bottom .26s cubic-bezier( .0, .0, .2, 1 ) );
                    }
                }
            }
        }
        @include typography-specs( 16px, 23px );
        padding: 0 10px;
        @include rounded-corner( 8px );
        z-index: 1;
        &::before {
            @include pseudo();
            @include dimensions( 10px, 10px );
            background: $color-green;
            z-index: -1;
        }
        &--hover {
            opacity: 0;
        }
        &--top {
            &::before {
                @include transform-position( false, false, -6px, false, rotate( 45deg ) translateX( -50% ) );
            }
            &--base {
                &::before {
                    left: 10px;
                }
            }
            &--center {
                &::before {
                    left: 50%;
                }
            }
        }
         &--bottom {
            &::before {
                @include transform-position( -6px, false, false, false, rotate( 45deg ) translateX( 50% ) );
            }
            &--base {
                &::before {
                    left: 10px;
                }
            }
            &--center {
                &::before {
                    left: 50%;
                }
            }
        }
        &--right {
            &::before {
                @include transform-position( 45%, false, false, 0, rotate( 45deg ) translateX( -50% ) );
            }
        }
        &--left {
            &::before {
                @include transform-position( 16%, -8px, false, false, rotate( -45deg ) translateX( -50% ) );
            }
        }
    }

    :hover > .tooltip--hover {
        opacity: 1;
    }
</style>